/**
 * AI OCR Service
 * Gerencia a extração de dados de notas fiscais via Gemini AI Vision.
 */
class AIOCRService {
    constructor() {
        this.apiKey = null;
        this._geminiModule = null;
    }

    async getGenerativeAI() {
        if (!this._geminiModule) {
            this._geminiModule = await import("@google/generative-ai");
        }
        return this._geminiModule;
    }

    setApiKey(key) {
        this.apiKey = key;
    }

    /**
     * Converte um arquivo de imagem para Base64.
     * @param {File} file Arquivo de imagem.
     * @returns {Promise<string>} String Base64.
     */
    fileToGenerativePart(file) {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => {
                const base64Data = reader.result.split(',')[1];
                resolve({
                    inlineData: {
                        data: base64Data,
                        mimeType: file.type
                    },
                });
            };
            reader.onerror = reject;
            reader.readAsDataURL(file);
        });
    }

    /**
     * Extrai dados da nota fiscal usando Gemini Vision.
     * @param {File} imageFile Arquivo da imagem da nota.
     * @returns {Promise<Object>} Dados extraídos.
     */
    async analyzeInvoice(imageFile) {
        if (!this.apiKey) {
            throw new Error('Chave da API Gemini não configurada.');
        }

        if (!imageFile) {
            throw new Error('Nenhuma imagem fornecida.');
        }

        // Import dinâmico centralizado para code-splitting
        const { GoogleGenerativeAI, SchemaType } = await this.getGenerativeAI();

        // Esquema estruturado para extração de nota fiscal
        const responseSchema = {
            type: SchemaType.OBJECT,
            properties: {
                amount: { type: SchemaType.NUMBER, description: "Valor total pago na nota fiscal" },
                date: { type: SchemaType.STRING, description: "Data da compra no formato YYYY-MM-DD" },
                description: { type: SchemaType.STRING, description: "Nome do estabelecimento ou posto" },
                category: { 
                    type: SchemaType.STRING, 
                    enum: ["Gasolina", "Etanol", "GNV", "Elétrico", "Alimentação", "Manutenção", "Lava Jato", "Outros"],
                    description: "Categoria que melhor descreve o gasto" 
                },
                fuelQuantity: { type: SchemaType.NUMBER, description: "Quantidade de litros, m3 ou kWh (se houver)" },
                fuelPrice: { type: SchemaType.NUMBER, description: "Preço unitário do combustível (se houver)" },
                paymentMethod: { 
                    type: SchemaType.STRING, 
                    enum: ["Cartão de Crédito", "Cartão de Débito", "Dinheiro", "Pix", "Vale/App Combustível", "Outro"],
                    description: "Forma de pagamento detectada" 
                }
            }
        };

        const tryAnalyze = async (modelName) => {
            const genAI = new GoogleGenerativeAI(this.apiKey);
            const model = genAI.getGenerativeModel({ 
                model: modelName,
                systemInstruction: "Você é um especialista em extração de dados de cupons fiscais brasileiros (NFC-e, SAT). Analise a imagem e extraia os dados. Se houver um QR Code na imagem, tente utilizá-lo para confirmar os dados. Extraia as informações rigorosamente no formato JSON. Se uma informação (como litros ou preço unitário) não estiver presente, omita o campo. Mapeie estabelecimentos de alimentação para Alimentação, postos para a categoria de combustível correspondente ou Outros se for loja de conveniência.",
                generationConfig: {
                    temperature: 0.1,
                    responseMimeType: "application/json",
                    responseSchema: responseSchema
                }
            });

            const imagePart = await this.fileToGenerativePart(imageFile);
            const prompt = "Analise esta nota fiscal, incluindo qualquer QR Code presente, e extraia as informações financeiras.";
            
            const result = await model.generateContent([prompt, imagePart]);
            const response = await result.response;
            return response.text().trim();
        };

        try {
            let text;
            try {
                console.log('AI_OCR: Tentando com gemini-2.5-flash...');
                text = await tryAnalyze("gemini-2.5-flash");
            } catch (e) {
                console.warn('AI_OCR: Falhou com gemini-2.5-flash, tentando fallback gemini-flash-latest...', e);
                text = await tryAnalyze("gemini-flash-latest");
            }
            
            console.log('AI_OCR: Resposta recebida:', text);
            return JSON.parse(text);
        } catch (error) {
            console.error('AI_OCR: Erro no processamento:', error);
            if (error.message?.includes('JSON')) {
                throw new Error('A IA não conseguiu formatar os dados. Tente uma foto mais nítida.');
            }
            throw new Error('Não foi possível ler esta nota fiscal. Verifique a iluminação e tente novamente.');
        }
    }

    /**
     * Extrai dados a partir do texto/URL decodificado de um QR Code fiscal.
     * @param {string} qrText Texto ou URL do QR Code.
     * @returns {Promise<Object>} Dados extraídos.
     */
    async analyzeQrText(qrText) {
        if (!this.apiKey) {
            throw new Error('Chave da API Gemini não configurada.');
        }

        const { GoogleGenerativeAI, SchemaType } = await this.getGenerativeAI();

        const responseSchema = {
            type: SchemaType.OBJECT,
            properties: {
                amount: { type: SchemaType.NUMBER, description: "Valor total pago na nota fiscal" },
                description: { type: SchemaType.STRING, description: "Nome do estabelecimento ou posto" },
                category: { 
                    type: SchemaType.STRING, 
                    enum: ["Gasolina", "Etanol", "GNV", "Elétrico", "Alimentação", "Manutenção", "Lava Jato", "Outros"],
                    description: "Categoria que melhor descreve o gasto" 
                }
            }
        };

        const genAI = new GoogleGenerativeAI(this.apiKey);
        const model = genAI.getGenerativeModel({ 
            model: "gemini-1.5-flash",
            systemInstruction: "Você é um especialista em extração de dados de cupons fiscais brasileiros. Analise o texto/URL decodificado de um QR Code de NFC-e/SAT e extraia o valor total pago, o nome do estabelecimento (se houver) e a categoria.",
            generationConfig: {
                temperature: 0.1,
                responseMimeType: "application/json",
                responseSchema: responseSchema
            }
        });

        const prompt = `Analise este texto de QR Code fiscal: "${qrText}"`;
        const result = await model.generateContent(prompt);
        const response = await result.response;
        return JSON.parse(response.text().trim());
    }
}

const aiOCRService = new AIOCRService();
export default aiOCRService;
